import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import axios from "axios";
import { path_url } from "../../Config/config";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaUser, FaClock, FaStickyNote } from "react-icons/fa";
import Task from "./Task";
import InteractionRecord from "./InteractionRecord";
import Service from "./Service";
import Quotation from "./Quotation";
import LeadTimeline from "../../components/lead/LeadTimeline";
import UserFooter from "../UserFooter";
import CreateTaskModal from "../components/CreateTaskModal";
import CreateInteractionRecordModal from "../components/CreateInteractionRecordModal";
import CreateServiceRequestModal from "../components/CreateServiceRequestModal";
import CreateQuotationModal from "../components/CreateQuotationModal";
import "../../Style/NewVendor.css";


const leadSchema = Yup.object().shape({
  product: Yup.string().required("Required"),
  preferences: Yup.string().required("Required"),
  status: Yup.string().required("Required"),
  notes: Yup.string(),
  direction: Yup.string().required("Required"),
});

function LeadDetails({ Details, refetchDetails }) {
  const [leadId, setLeadId] = useState("");

  // Modal visibility states
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [isInteractionModalOpen, setIsInteractionModalOpen] = useState(false);
  const [isServiceModalOpen, setIsServiceModalOpen] = useState(false);
  const [isQuotationModalOpen, setIsQuotationModalOpen] = useState(false);

  // Section data states for immediate real-time updates
  const [taskList, setTaskList] = useState([]);
  const [interactionRecordList, setInteractionRecordList] = useState([]);
  const [serviceRequestList, setServiceRequestList] = useState([]);
  const [quotationList, setQuotationList] = useState([]);

  // Notes state
  const [extraNotes, setExtraNotes] = useState([]);
  const [newNoteText, setNewNoteText] = useState("");
  const [noteSubmitting, setNoteSubmitting] = useState(false);

  useEffect(() => {
    if (Details && Details.lead && Details.lead.id) {
      setLeadId(Details.lead.id); // Update the leadId state
    }
    if (Details && Details.eventList) {
      if (Details.eventList.taskList) setTaskList(Details.eventList.taskList);
      if (Details.eventList.interactionRecordList) setInteractionRecordList(Details.eventList.interactionRecordList);
      if (Details.eventList.serviceRequestList) setServiceRequestList(Details.eventList.serviceRequestList);
      if (Details.eventList.quotationList) setQuotationList(Details.eventList.quotationList);
    }
    setExtraNotes([]);
  }, [Details]);

  const handleTaskCreated = (newTask) => {
    setTaskList((prev) => [newTask, ...prev]);
  };

  const handleInteractionCreated = (newRecord) => {
    setInteractionRecordList((prev) => [newRecord, ...prev]);
  };

  const handleServiceCreated = (newService) => {
    setServiceRequestList((prev) => [newService, ...prev]);
  };

  const handleQuotationCreated = (newQuotation) => {
    setQuotationList((prev) => [newQuotation, ...prev]);
  };

  const options = [
    { value: "Inbound", label: "Inbound" },
    { value: "Outbound", label: "Outbound" },
  ];

  const options1 = [
    { value: "Emergency", label: "Emergency" },
    { value: "High", label: "High" },
    { value: "Low", label: "Low" },
    { value: "Medium", label: "Medium" },
    { value: "Normal", label: "Normal" },
    { value: "Deferred", label: "Deferred" },
    { value: "Critical", label: "Critical" },
  ];

  const options2 = [
    { value: "Close", label: "Close" },
    { value: "Open", label: "Open" },
  ];

  const formatDate = (dateVal) => {
    if (!dateVal) return "N/A";
    try {
      const d = new Date(dateVal);
      return isNaN(d.getTime()) ? dateVal : d.toLocaleString();
    } catch {
      return dateVal;
    }
  };

  // ── Notes logic ──────────────────────────────────────────────
  const baseNotes = (Details?.eventList?.interactionRecordList || []).filter(
    (record) =>
      record.notes ||
      record.remarks ||
      record.description ||
      record.enquiry
  );

  const sortedBaseNotes = [...baseNotes].sort((a, b) => {
    const dateA = new Date(a.createdAt || a.date || a.interactionDate || 0);
    const dateB = new Date(b.createdAt || b.date || b.interactionDate || 0);
    return dateB - dateA;
  });

  const allNotes = [...extraNotes, ...sortedBaseNotes];

  const handleAddNote = async () => {
    if (!newNoteText.trim()) return;
    setNoteSubmitting(true);
    const token = localStorage.getItem("accessToken");
    const now = new Date();
    const payload = {
      enquiry: newNoteText.trim(),
      leadid: Details?.lead?.id || "",
    };
    try {
      const response = await axios.post(`${path_url}/interactionrecord`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.data.statusCodeValue === 200 || response.status === 200) {
        toast.success("Note added successfully");
        setExtraNotes((prev) => [
          {
            id: Date.now(),
            notes: newNoteText.trim(),
            createdBy: "You",
            createdAt: now.toISOString(),
          },
          ...prev,
        ]);
        setNewNoteText("");
        if (typeof refetchDetails === "function") refetchDetails();
      } else {
        setExtraNotes((prev) => [
          {
            id: Date.now(),
            notes: newNoteText.trim(),
            createdBy: "You",
            createdAt: now.toISOString(),
          },
          ...prev,
        ]);
        setNewNoteText("");
        toast.warning("Note saved locally. Backend sync pending.");
      }
    } catch {
      setExtraNotes((prev) => [
        {
          id: Date.now(),
          notes: newNoteText.trim(),
          createdBy: "You",
          createdAt: now.toISOString(),
        },
        ...prev,
      ]);
      setNewNoteText("");
      toast.warning("Note saved locally. Check your connection.");
    } finally {
      setNoteSubmitting(false);
    }
  };

  if (!Details) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <Formik
        enableReinitialize={true}
        initialValues={{
          preferences: "Medium",
          product: "CRM",
          status: "Close",
          direction: "Outbound",
          notes: "",
          leadid: leadId || "default_lead_id",
        }}
        validationSchema={leadSchema}
        onSubmit={async (values) => {
          try {
            const token = localStorage.getItem("accessToken");
            const response = await axios.post(
              `${path_url}/interactionrecord`,
              values,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  "Content-Type": "application/json",
                },
              }
            );
            if (response.data.statusCodeValue === 200) {
              toast.success("Interaction record submitted successfully");
              if (typeof refetchDetails === "function") refetchDetails();
            } else {
              toast.error(response.data.body);
            }
          } catch (errors) {
            console.log(errors.message);
          }
        }}
      >
        {(formik) => (
          <form onSubmit={formik.handleSubmit}>
            <fieldset
              style={{ paddingLeft: "5px", width: "98%", marginBottom: "5rem" }}
            >
              <div className="mt-3">
                <div className="row">
                  {/* Interaction Record Panel */}
                  <div className="col-lg-6 mb-3">
                    <div className="ir-notes-panel">
                      <div className="ir-notes-panel-title">
                        📋 Interaction Record
                      </div>

                      <div className="ir-field">
                        <label className="form-label">Direction</label>
                        <select
                          className="form-select form-select-sm"
                          name="direction"
                          id="direction"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.direction}
                        >
                          {options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="ir-grid-2">
                        <div>
                          <label className="form-label">Status</label>
                          <select
                            className="form-select form-select-sm"
                            name="status"
                            id="status"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.status}
                          >
                            {options2.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="form-label">Priority</label>
                          <select
                            className="form-select form-select-sm"
                            name="preferences"
                            id="priority"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.preferences}
                          >
                            {options1.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="ir-field">
                        <label className="form-label">Product</label>
                        <select
                          className="form-select form-select-sm"
                          name="product"
                          id="product"
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.product}
                        >
                          {options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="ir-field" style={{ flex: 1 }}>
                        <label className="form-label">Notes</label>
                        <textarea
                          className="form-control form-control-sm"
                          id="notes"
                          name="notes"
                          rows={3}
                          placeholder="Enter interaction notes..."
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          value={formik.values.notes}
                          style={{ resize: "none" }}
                        />
                      </div>

                      <div className="text-end mt-2">
                        <button
                          type="submit"
                          className="btn btn-sm btn-info text-white px-4"
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Notes Panel */}
                  <div className="col-lg-6 mb-3">
                    <div className="ir-notes-panel">
                      <div className="notes-section-header">
                        <FaStickyNote style={{ color: "#0dcaf0" }} />
                        Notes
                      </div>

                      <div className="notes-list">
                        {allNotes.length === 0 ? (
                          <p className="notes-empty">
                            No notes yet. Write below to add your first note.
                          </p>
                        ) : (
                          allNotes.map((record, index) => {
                            const noteText =
                              record.notes ||
                              record.content ||
                              record.remarks ||
                              record.description ||
                              record.enquiry;
                            let author =
                              record.createdBy || record.assigned || "System";
                            if (author === localStorage.getItem("userId")) {
                              author = localStorage.getItem("name") || "You";
                            }
                            const dateVal =
                              record.createdAt ||
                              record.creationDate ||
                              record.date ||
                              record.interactionDate;
                            return (
                              <div key={record.id || index} className="note-card">
                                <div className="note-card-header">
                                  <span className="note-card-author">
                                    <FaUser
                                      className="me-1"
                                      style={{ fontSize: "0.72rem" }}
                                    />
                                    {author}
                                  </span>
                                  <span className="note-card-date">
                                    <FaClock
                                      className="me-1"
                                      style={{ fontSize: "0.68rem" }}
                                    />
                                    {formatDate(dateVal)}
                                  </span>
                                </div>
                                <div className="note-card-body">
                                  <div className="note-card-content">
                                    {noteText}
                                  </div>
                                  {record.updatedAt && (
                                    <div className="note-card-updated">
                                      Last Updated: {formatDate(record.updatedAt)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {/* Inline composer */}
                      <div className="note-composer">
                        <textarea
                          placeholder="Write a note and click Save…"
                          value={newNoteText}
                          onChange={(e) => setNewNoteText(e.target.value)}
                          disabled={noteSubmitting}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey))
                              handleAddNote();
                          }}
                        />
                        <div className="note-composer-actions">
                          <button
                            type="button"
                            className="note-save-btn"
                            onClick={handleAddNote}
                            disabled={noteSubmitting || !newNoteText.trim()}
                          >
                            {noteSubmitting ? "Saving…" : "Save Note"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </fieldset>
          </form>
        )}
      </Formik>

      {/* Creation Modals */}
      <CreateTaskModal
        open={isTaskModalOpen}
        onClose={() => setIsTaskModalOpen(false)}
        leadId={leadId}
        onTaskCreated={handleTaskCreated}
      />
      <CreateInteractionRecordModal
        open={isInteractionModalOpen}
        onClose={() => setIsInteractionModalOpen(false)}
        leadId={leadId}
        onInteractionCreated={handleInteractionCreated}
      />
      <CreateServiceRequestModal
        open={isServiceModalOpen}
        onClose={() => setIsServiceModalOpen(false)}
        leadId={leadId}
        onServiceCreated={handleServiceCreated}
      />
      <CreateQuotationModal
        open={isQuotationModalOpen}
        onClose={() => setIsQuotationModalOpen(false)}
        leadId={leadId}
        onQuotationCreated={handleQuotationCreated}
      />

      <UserFooter />
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
}

export default LeadDetails;